import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Skeleton } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { setFiltersAction, setTeamAction } from 'actions/globalFilters';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useToast } from 'components/hooks/useToast';
import TeamModel from 'models/team';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';
import { INSIGHT_VIEWS } from 'reducers/okrsPageSlice';
import { useSelectedView } from 'reducers/viewsSlice';
import { getCompletedOnDurationFromFilters } from 'screens/Initiatives/helpers';
import { getDefaultLocalStorageStatsFilters, useTeamPageState } from 'screens/Team';
import buildTeamPageFilters from 'screens/Team/TeamPageFilters/buildTeamPageFilters';
import { okrsInsightsSelector } from 'selectors/okrsInsightsSelector';
import { okrsPageSelector } from 'selectors/okrsPage';
import { baseTheme } from 'themes/clients/baseTheme';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';
import { checkUserIsGoalAdmin } from 'utils/helpers/rolesAccess';

import {
  Archive,
  CollapseAllList,
  Download,
  ExpandAllList,
  ExpandAllListStroke,
  Refresh2,
} from 'assets/assetsV1/iconComponents';

import { TASK_TYPES_2, TOAST_STATUSES, VIEW_DOWNLOAD_INSIGHT_PRIVILEGES } from 'constants/ProjectConstants';
import { modifyString, StringModifier } from 'constants/copyHelpers';
import { sendClickedRefreshEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';
import { routes } from 'constants/routes';

import { ArchivedGoals } from '../Archived';
import OKRCardView from '../Card';
import { GROUP_BY_INDIVIDUAL_KEY_OBJ, SPLIT_BY_NONE_KEY_OBJ } from '../Insights';
import ClickThroughView from '../Insights/ClickThroughView';
import GroupByIndividualInsightsTable from '../Insights/RabbitGroupByIndividualInsightsTable';
import TreeView from '../Tree/RabbitTreeView';
import { setIsArchivedView, useSelectIsArchivedViewVisible } from '../slice/GoalsSlice';
import { GoalsTreeViewTable } from './GoalsTreeViewTable';
import { GroupByGoalGraphs } from './components/Graphs/GroupByGoal/GroupByGoalGraphs';
import { GroupByIndividualGraphs } from './components/Graphs/GroupByIndividual/GroupByIndividualGraphs';
import { ManageGoalsFilterBar } from './components/ManageGoalsFilterBar';
import { GroupByGoalMetricsCards } from './components/MetricsCards/GroupByGoal/GroupByGoalMetricsCards';
import { GroupByIndividualMetricsCards } from './components/MetricsCards/GroupByIndividual/GroupByIndividualMetricsCards';
import { OKRSidePanels } from './components/OKRSidePanels';
import { goalsCascadedViewReactQueryKey } from './hooks/dataHooks';
import { useGroupByGoalsData } from './hooks/useGroupByGoalsData';
import { useGroupByIndividualData } from './hooks/useGroupByIndividualData';
import { useOKRManageViewType } from './hooks/useOKRManageViewType';
import { generateGoalsExploreCSVData, generateIndividualInsightsCSVData } from './utils/generateCSVForGoals';

const GROUP_BY = {
  uuid: null,
  keyName: 'objective',
  displayName: 'Objective',
  enabled: true,
};

const SPLIT_BY = {
  uuid: null,
  keyName: 'none',
  displayName: 'None',
  enabled: true,
};

const generateAttributeFilter = (activeFilters = []) => {
  const data = activeFilters.map(({ value }) => value);
  return {
    type: 'STATUS',
    operatorType: 'IN',
    data,
  };
};

const AgGridTreeView = ({
  showHierarchyToggle,
  showGraphs = true,
  showMetrics = true,
  hideArchivedGoalsOption = false,
}) => {
  const gridRef = useRef();
  const page = useViewPageName();
  const toast = useToast();

  // const { showParentObjectives: showParentObjectivesParam } = useQuery();

  // const isShowParentObjectives = showParentObjectivesParam === 'true';

  const sideNavSelectedCohort = useSelectedView();
  const { selectedTimelines } = useSelector(okrsPageSelector);

  const [isOpenCreateGoalPanel, setIsOpenCreateGoalPanel] = useState(false);
  const [isOpenCreateObjectivePanel, setIsOpenCreateObjectivePanel] = useState(false);

  const location = useLocation();

  // const isHierarchyView = checkNewNavEnabled() ? isShowParentObjectives : Boolean(isAuditRoute);

  const {
    cardKPIData: groupByGoalCardKPIData,
    chartData: groupByGoalChartData,
    isLoading: groupByGoalIsLoading,
    setChartData: setGroupByGoalChartData,
    onModalUpdated: onGroupByGoalModalUpdated,
    donutChartData: groupByGoalDonutChartData,
    setRowData: groupByGoalSetRowData,
  } = useGroupByGoalsData({
    gridRef,
  });

  const {
    cardKPIData: groupByIndividualCardKPIData,
    chartData: groupByIndividualGoalsData,
    isLoading: groupByIndividualIsLoading,
    setChartData: setGroupByIndividualChartData,
    onModalUpdated: onGroupByIndividualModalUpdated,
  } = useGroupByIndividualData({
    gridRef,
  });

  /*  read keyResultsName and objectivesName from new config */
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const { id: paramsTeamId, name: teamName } = sideNavSelectedCohort ?? {};
  const dispatch = useDispatch();

  const { viewType: groupByType, isInsightsClickThroughViewActive } = useSelector(okrsInsightsSelector);

  const isGroupByGoals = groupByType === INSIGHT_VIEWS[1].value;
  const isGroupByIndividual = groupByType === INSIGHT_VIEWS[0].value;

  const employeeGoalsAccess = useSelector((state) => state.employeeGoalsAccessPrivileges) ?? {};

  const canDownloadIndividualInsights = employeeGoalsAccess.includes(
    VIEW_DOWNLOAD_INSIGHT_PRIVILEGES.DOWNLOAD_INDIVIDUAL_INSIGHTS
  );

  const canDownloadObjectiveInsights = employeeGoalsAccess.includes(
    VIEW_DOWNLOAD_INSIGHT_PRIVILEGES.DOWNLOAD_OBJECTIVE_LEVEL_INSIGHTS
  );

  const {
    team,
    setTeam,
    activeFilters,
    refreshData,
    loggedInEmployee,
    selectedTaskType,
    activeFiltersDispatch,
    newFiltersState,
    setRefreshData,
  } = useTeamPageState({
    sideNavSelectedCohort,
  });

  const attributeFilter = generateAttributeFilter(activeFilters);

  const [, setDuration] = useState(getCompletedOnDurationFromFilters(newFiltersState));
  const isArchivedView = useSelectIsArchivedViewVisible();

  const { viewType: OKRManageView, replaceViewType, setViewType } = useOKRManageViewType();

  const isHierarchyView = OKRManageView === 'listWithHierarchy';

  useEffect(() => {
    if (!OKRManageView) {
      replaceViewType('list');
    }
  }, [OKRManageView, replaceViewType]);

  useEffect(() => {
    // when sideNavSelectedCohort changes, reset the view type to list
    if (OKRManageView !== 'list' && !checkNewNavEnabled()) {
      setViewType('list');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sideNavSelectedCohort?.id, checkNewNavEnabled]);

  // tree view state
  const [, setTimeboundDetailsOfCreatedObjective] = useState(null);
  const [isOpenCreateGoalTreeViewPanel, setIsOpenCreateGoalTreeViewPanel] = useState(false);

  const [, setIsGoalPanelOfCreatedObjective] = useState(false);

  const refresh = () => {
    setRefreshData(true);
    setTimeout(() => {
      setRefreshData(false);
    }, 100);
  };

  const queryClient = useQueryClient();

  const refetchCascadedViewData = () => {
    queryClient.invalidateQueries(
      goalsCascadedViewReactQueryKey({
        cohort: sideNavSelectedCohort,
        groupByKeys: GROUP_BY,
        splitByKeys: SPLIT_BY,
        orderBy: null,
        timeBounds: [selectedTimelines?.[0]],
        attributeFilter,
        isHierarchyView,
        pageType: 'TEAM_HUB',
      })
    );
  };

  useEffect(() => {
    const teamId = paramsTeamId;
    if (!teamId) return;

    dispatch(setTeamAction(teamId)).then(() => {
      setTeam(new TeamModel({ id: teamId, name: teamName }));
    });
  }, [dispatch, setTeam, paramsTeamId, teamName]);

  useEffect(() => {
    const newActiveFilters = buildTeamPageFilters(newFiltersState, true);
    setDuration((duration) => {
      const newDuration = getCompletedOnDurationFromFilters(newFiltersState);
      return JSON.stringify(duration) !== JSON.stringify(newDuration) ? newDuration : duration;
    });
    const localStorageStatsFilters = getDefaultLocalStorageStatsFilters(TASK_TYPES_2.goal) || [];
    activeFiltersDispatch(setFiltersAction([...newActiveFilters, ...localStorageStatsFilters]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(newFiltersState)]);

  const [isRefreshing, setIsRefreshing] = useState(false);

  const refreshCascadedViewData = useCallback(async () => {
    sendClickedRefreshEvent({ eventProperties: { page } });
    if (isRefreshing) {
      return;
    }
    try {
      setIsRefreshing(true);
      await queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsCascadedData]);
    } catch (error) {
      toast({
        title: 'Error reloading. Please try again.',
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
      console.error('Error reloading cascaded view data');
    } finally {
      setIsRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshing, queryClient, page]);

  const cardTreeViewOptions = useMemo(
    () => [
      {
        label: 'Refresh list',
        onClick: refreshCascadedViewData,
        Icon: Refresh2,
      },
    ],
    [refreshCascadedViewData]
  );

  const listViewOptions = useMemo(() => {
    const options = [
      {
        label: `Expand/ collapse all ${modifyString(keyResultsName, StringModifier.PLURAL_SENTENCE_CASE)}`,
        type: 'subMenu',
        onClick: () => {},
        subOptions: [
          {
            label: `Expand all ${modifyString(keyResultsName, StringModifier.PLURAL_SENTENCE_CASE)}`,
            onClick: () => gridRef.current.api.expandAll(),
            Icon: ExpandAllList,
          },
          {
            label: `Collapse all ${modifyString(keyResultsName, StringModifier.PLURAL_SENTENCE_CASE)}`,
            onClick: () => gridRef.current.api.collapseAll(),
            Icon: CollapseAllList,
          },
        ],
        Icon: ExpandAllListStroke,
      },
      {
        label: 'Refresh list',
        onClick: refreshCascadedViewData,
        Icon: Refresh2,
      },
      {
        label: 'Download report',
        Icon: Download,
        type: 'subMenu',
        subOptions: [
          {
            label: 'Current View Download',
            onClick: () => {
              if (isGroupByGoals) {
                generateGoalsExploreCSVData({
                  exportedRows: 'filteredAndSorted',
                  gridRef,
                  keyResultsName,
                  moduleName,
                  objectivesName,
                  sideNavSelectedCohort,
                });
              } else {
                generateIndividualInsightsCSVData({
                  exportedRows: 'filteredAndSorted',
                  gridRef,
                  sideNavSelectedCohort,
                });
              }
            },
            description: `Download selected metrics for filtered list of ${modifyString(
              moduleName,
              StringModifier.PLURAL_SENTENCE_CASE
            )} along with basic ${modifyString(moduleName, StringModifier.SENTENCE_CASE)} details`,
          },
          {
            label: 'Detailed Download',
            onClick: () => {
              if (isGroupByGoals) {
                generateGoalsExploreCSVData({
                  exportedRows: 'all',
                  gridRef,
                  keyResultsName,
                  moduleName,
                  objectivesName,
                  sideNavSelectedCohort,
                });
              } else {
                generateIndividualInsightsCSVData({
                  exportedRows: 'filteredAndSorted',
                  gridRef,
                  sideNavSelectedCohort,
                });
              }
            },
            description: `Download all the metrics for all the ${modifyString(
              moduleName,
              StringModifier.PLURAL_SENTENCE_CASE
            )} along with basic ${modifyString(moduleName, StringModifier.SENTENCE_CASE)} details`,
          },
        ],
      },
      {
        label: `View archived ${modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)}`,
        onClick: () => dispatch(setIsArchivedView({ isArchivedView: !isArchivedView })),
        type: 'radio',
        value: 'showArchivedGoals',
        Icon: Archive,
      },
    ];

    // if it's not my data then there's no restriction on downloading
    // else it depends on access privileges
    if (sideNavSelectedCohort?.id !== -12) {
      if (isGroupByIndividual && !canDownloadIndividualInsights) {
        options.splice(2, 1);
      }
      if (isGroupByGoals && !canDownloadObjectiveInsights) {
        options.splice(2, 1);
      }
    }

    if (isGroupByIndividual) {
      // remove the 1st option as it is not required for individual insights
      options.shift();
    }

    // if user is not goal admin or not in company cohort then remove archived goals option
    if (!checkUserIsGoalAdmin() || sideNavSelectedCohort?.id !== -1 || hideArchivedGoalsOption) {
      options.pop();
    }

    return options;
  }, [
    canDownloadIndividualInsights,
    canDownloadObjectiveInsights,
    dispatch,
    hideArchivedGoalsOption,
    isArchivedView,
    isGroupByGoals,
    isGroupByIndividual,
    keyResultsName,
    moduleName,
    objectivesName,
    refreshCascadedViewData,
    sideNavSelectedCohort,
  ]);

  const ellipsisMenuOptions = useMemo(() => {
    if (['tree', 'card'].includes(OKRManageView)) {
      return cardTreeViewOptions;
    }
    return listViewOptions;
  }, [cardTreeViewOptions, listViewOptions, OKRManageView]);

  if (!sideNavSelectedCohort || !team || !team?.id) {
    return <Skeleton height="30vh" />;
  }

  const showParentObjectives = Boolean(
    matchPath(location.pathname, {
      path: routes.goals.audit,
      exact: false,
    })
  );

  const showChartsAndMetrics = (OKRManageView === 'list' || OKRManageView === 'listWithHierarchy') && !isArchivedView;

  if (isInsightsClickThroughViewActive) return <ClickThroughView />;

  return (
    <>
      <Box width="100%" p={checkNewNavEnabled() ? 0 : '16px'} mt={1}>
        {isGroupByGoals && showChartsAndMetrics ? (
          <>
            <GroupByGoalGraphs
              chartData={groupByGoalChartData}
              showGraphs={showGraphs}
              donutChartData={groupByGoalDonutChartData}
            />
            <GroupByGoalMetricsCards
              cardKPIData={groupByGoalCardKPIData}
              isLoading={groupByGoalIsLoading}
              showMetrics={showMetrics}
            />
          </>
        ) : null}
        {isGroupByIndividual && showChartsAndMetrics && showGraphs ? (
          <>
            <GroupByIndividualGraphs
              goalsOwnedChartData={groupByIndividualGoalsData}
              gridRef={gridRef}
              keyResultsName={keyResultsName}
            />
            <GroupByIndividualMetricsCards
              cardKPIData={groupByIndividualCardKPIData}
              isLoading={groupByIndividualIsLoading}
            />
          </>
        ) : null}

        <Box background="white" p={3} width="100%" borderRadius="8px" boxShadow={baseTheme.shadows.paper}>
          {!isArchivedView && (
            <ManageGoalsFilterBar
              ellipsisMenuOptions={ellipsisMenuOptions}
              showHierarchyToggle={showHierarchyToggle}
              gridRef={gridRef}
              refetchCascadedViewData={refetchCascadedViewData}
              showArchivedGoals={isArchivedView}
            />
          )}

          {OKRManageView === 'list' || OKRManageView === 'listWithHierarchy' ? (
            <>
              {!isArchivedView && isGroupByGoals && (
                <GoalsTreeViewTable
                  showParentObjectives={showParentObjectives}
                  attributeFilter={attributeFilter}
                  timeBoundPeriod={selectedTimelines?.[0] ?? null}
                  type={selectedTaskType}
                  isHierarchyView={isHierarchyView}
                  gridRef={gridRef}
                  setChartData={setGroupByGoalChartData}
                  setRowData={groupByGoalSetRowData}
                  onModalUpdated={onGroupByGoalModalUpdated}
                />
              )}

              {!isArchivedView && isGroupByIndividual && (
                <GroupByIndividualInsightsTable
                  selectedCohort={sideNavSelectedCohort}
                  gridRef={gridRef}
                  selectedTimeBoundPeriods={selectedTimelines}
                  selectedGroupByKey={GROUP_BY_INDIVIDUAL_KEY_OBJ}
                  selectedSplitByKey={SPLIT_BY_NONE_KEY_OBJ}
                  setChartData={setGroupByIndividualChartData}
                  onModalUpdated={onGroupByIndividualModalUpdated}
                />
              )}

              {isArchivedView && <ArchivedGoals showArchivedGoals={isArchivedView} renderBorderlessTable />}
            </>
          ) : null}

          {OKRManageView === 'card' ? <OKRCardView /> : null}

          {OKRManageView === 'tree' ? (
            <TreeView
              isOpenCreateGoalPanel={isOpenCreateGoalTreeViewPanel}
              setIsOpenCreateGoalPanel={setIsOpenCreateGoalTreeViewPanel}
              employee={loggedInEmployee}
              selectedTimeBoundPeriod={selectedTimelines?.[0] ?? null}
              refreshData={refreshData}
              refreshFn={refresh}
              teamId={team.id}
              isOpenCreateObjectivePanel={isOpenCreateObjectivePanel}
              setIsOpenCreateObjectivePanel={setIsOpenCreateObjectivePanel}
              selectedCohort={sideNavSelectedCohort}
              setIsGoalPanelOfCreatedObjective={setIsGoalPanelOfCreatedObjective}
              setTimeboundDetailsOfCreatedObjective={setTimeboundDetailsOfCreatedObjective}
            />
          ) : null}
        </Box>
      </Box>
      <OKRSidePanels
        isOpenCreateGoalPanel={isOpenCreateGoalPanel}
        setIsOpenCreateGoalPanel={setIsOpenCreateGoalPanel}
        isOpenCreateObjectivePanel={isOpenCreateObjectivePanel}
        setIsOpenCreateObjectivePanel={setIsOpenCreateObjectivePanel}
        selectedTimelines={selectedTimelines}
        afterGoalCreation={refetchCascadedViewData}
      />
    </>
  );
};

export { AgGridTreeView };
